import React, { useState, useEffect } from 'react';
import Icon from '@components/atoms/icon/icon';
import { ListingContext } from '@context/listing.context';
import { useContextSelector } from 'use-context-selector';
import clsx from 'clsx';
import styles from '../../dropdown.module.scss';

interface CategoriesMenuInterface {
  setNbCatDisplayed: (items: number) => void;
  setCloseMenu: (close: boolean) => void;
}

const CategoriesMenu: React.FC<CategoriesMenuInterface> = ({ setNbCatDisplayed, setCloseMenu }): React.JSX.Element => {
  const { categories, listingDraftPayload, setListingDraftAttribute } = useContextSelector(
    ListingContext,
    (state) => state,
  );

  /**
   * openMenu contains the number of the submenu which should be open.
   * If any submenu is open, openMenu = 0.
   */
  const [openMenu, setOpenMenu] = useState(0);

  /**
   * Toggle menu by setting the openMenu variable
   * @param e // click event = current target to get the current classes list
   * @param index // number of the item, starting with "1"
   */
  const handleToggleSubmenu = (e, index) => {
    const param = e.currentTarget.classList.contains(styles.open) ? 0 : index;
    setOpenMenu(param);
  };

  /**
   * Check if the subcategory is in the category
   * @param cat category tested
   * @param subcat idof the subcategory selected
   * @returns boolean
   */
  const isSubcatInCat = (cat, subcat): boolean => {
    const obj = cat?.subCategories?.find((sc) => sc.id === subcat);
    return !!obj;
  };

  useEffect(() => {
    const nbOfCat = categories?.length;
    const nbOfSubcat = categories?.[openMenu - 1]?.subCategories?.length;
    setNbCatDisplayed(openMenu > 0 ? nbOfSubcat + 1 : nbOfCat);
  }, [openMenu]);

  return (
    <ul
      className={styles.dropdownMenu}
      tabIndex={-1}
      role="menu"
      aria-label="choose your Category"
    >
      {categories.map((category, index) => (
        <li
          tabIndex={0}
          role="menuitem"
          key={`category_${index + 1}`}
          className={clsx(openMenu === index + 1 && styles.open)}
          onClick={(e) => {
            handleToggleSubmenu(e, index + 1);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleToggleSubmenu(e, index + 1);
            }
          }}
        >
          <button
            type="button"
            className={clsx(styles.dropdownMenu__option, {
              [styles.selected]: isSubcatInCat(category, listingDraftPayload?.category),
            })}
          >
            <span
              className={styles.dropdownMenu__option__text}
              id={`category${index}`}
            >
              {category.title}
            </span>
            <Icon
              name="ios-arrow-right-20"
              className={styles.dropdownMenu__option__icon}
            />
          </button>
          {category.subCategories && (
            <ul
              className={styles.dropdownMenu__submenu}
              tabIndex={-1}
              role="listbox"
              aria-labelledby={`category${index + 1}`}
            >
              {category?.subCategories.map((subcat) => (
                <li
                  tabIndex={0}
                  role="option"
                  key={`subcategory_${subcat.id}`}
                  className={clsx(
                    styles.dropdownMenu__option,
                    listingDraftPayload.category === subcat.id && styles.selected,
                  )}
                  onClick={() => {
                    setListingDraftAttribute({ category: subcat.id });
                    setCloseMenu(true);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setListingDraftAttribute({ category: subcat.id });
                      setCloseMenu(true);
                    }
                  }}
                  aria-selected={listingDraftPayload.category === subcat.id}
                >
                  {subcat?.displayValue ? subcat?.displayValue : subcat.title}
                </li>
              ))}
            </ul>
          )}
        </li>
      ))}
    </ul>
  );
};

export default CategoriesMenu;
